import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Clock, Bell, BellOff, AlertCircle, CheckCircle, XCircle, Mail } from 'lucide-react';
import notificationService from '../../services/notificationService';
import Header from "../../components/ui/Header";

const StudentNotificationCenter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Notification history state
  const [notifications, setNotifications] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Notification preferences state
  const [preferences, setPreferences] = useState({
    emailEnabled: true,
    newSongsEnabled: true,
    newLessonsEnabled: true,
    newContentEnabled: true
  });
  const [preferencesLoading, setPreferencesLoading] = useState(true);
  const [savingPreferences, setSavingPreferences] = useState(false);

  // Load notification history
  useEffect(() => {
    if (user?.id) {
      loadNotificationHistory();
    }
  }, [user]);

  // Load notification preferences
  useEffect(() => {
    if (user?.id) {
      loadPreferences();
    }
  }, [user]);

  const loadNotificationHistory = async () => {
    try {
      setHistoryLoading(true);
      const history = await notificationService?.getHistory(user?.id, 50);
      setNotifications(history);
    } catch (err) {
      console.error('Error loading notification history:', err);
      setError('Failed to load notification history');
    } finally {
      setHistoryLoading(false);
      setLoading(false);
    }
  };

  const loadPreferences = async () => {
    try {
      setPreferencesLoading(true);
      const prefs = await notificationService?.getPreferences(user?.id);
      setPreferences(prefs);
    } catch (err) {
      console.error('Error loading preferences:', err);
      // Don't show error for missing preferences, just use defaults
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handlePreferenceToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev?.[key]
    }));
  };

  const handleSavePreferences = async () => {
    try {
      setSavingPreferences(true);
      setError('');
      setSuccessMessage('');

      await notificationService?.updatePreferences(user?.id, preferences);
      
      setSuccessMessage('Notification preferences saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError('Failed to save preferences. Please try again.');
    } finally {
      setSavingPreferences(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': case'delivered':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getNotificationTypeLabel = (type) => {
    switch (type) {
      case 'new_song':
        return 'New Song';
      case 'new_lesson':
        return 'New Lesson';
      case 'new_content':
        return 'New Content';
      default:
        return type;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(date);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-white text-xl">Loading notification center...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Notification Center</h1>
          <p className="text-white/80">Manage your email notifications and view delivery history</p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-500/20 border border-green-500/50 rounded-2xl p-4">
            <div className="flex items-center gap-3 text-white">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>{successMessage}</span>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-500/20 border border-red-500/50 rounded-2xl p-4">
            <div className="flex items-center gap-3 text-white">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Notification Preferences - Left Column */}
          <div className="lg:col-span-1">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
              <div className="flex items-center gap-3 mb-6">
                <Bell className="w-6 h-6 text-purple-300" />
                <h2 className="text-2xl font-bold text-white">Notification Preferences</h2>
              </div>

              {preferencesLoading ? (
                <div className="text-white/60 text-center py-8">Loading preferences...</div>
              ) : (
                <div className="space-y-4">
                  {/* Email Notifications Master Toggle */}
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-purple-300" />
                        <label className="text-white font-semibold">Email Notifications</label>
                      </div>
                      <button
                        onClick={() => handlePreferenceToggle('emailEnabled')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          preferences?.emailEnabled ? 'bg-purple-500' : 'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences?.emailEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <p className="text-white/60 text-sm">Enable or disable all email notifications</p>
                  </div>

                  {/* Individual Notification Types */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between bg-white/5 rounded-xl p-4 border border-white/10">
                      <label className="text-white">New Songs</label>
                      <button
                        onClick={() => handlePreferenceToggle('newSongsEnabled')}
                        disabled={!preferences?.emailEnabled}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          preferences?.newSongsEnabled && preferences?.emailEnabled ? 'bg-purple-500' : 'bg-gray-600'
                        } ${!preferences?.emailEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences?.newSongsEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between bg-white/5 rounded-xl p-4 border border-white/10">
                      <label className="text-white">New Lessons</label>
                      <button
                        onClick={() => handlePreferenceToggle('newLessonsEnabled')}
                        disabled={!preferences?.emailEnabled}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          preferences?.newLessonsEnabled && preferences?.emailEnabled ? 'bg-purple-500' : 'bg-gray-600'
                        } ${!preferences?.emailEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences?.newLessonsEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between bg-white/5 rounded-xl p-4 border border-white/10">
                      <label className="text-white">General Content Updates</label>
                      <button
                        onClick={() => handlePreferenceToggle('newContentEnabled')}
                        disabled={!preferences?.emailEnabled}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          preferences?.newContentEnabled && preferences?.emailEnabled ? 'bg-purple-500' : 'bg-gray-600'
                        } ${!preferences?.emailEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences?.newContentEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Save Button */}
                  <button
                    onClick={handleSavePreferences}
                    disabled={savingPreferences}
                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl transition-colors"
                  >
                    {savingPreferences ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Notification History - Right Column */}
          <div className="lg:col-span-2">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-6 h-6 text-purple-300" />
                <h2 className="text-2xl font-bold text-white">Notification History</h2>
              </div>

              {historyLoading ? (
                <div className="text-white/60 text-center py-8">Loading notification history...</div>
              ) : notifications?.length === 0 ? (
                <div className="text-center py-12">
                  <BellOff className="w-16 h-16 text-white/30 mx-auto mb-4" />
                  <p className="text-white/60 text-lg">No notifications yet</p>
                  <p className="text-white/40 text-sm mt-2">
                    You'll see notifications here when new content is added to your favorite musicals
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                  {notifications?.map((notification) => (
                    <div
                      key={notification?.id}
                      className="bg-white/5 rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 mt-1">
                          {getStatusIcon(notification?.emailStatus)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <div>
                              <span className="inline-block bg-purple-500/20 text-purple-300 text-xs font-semibold px-2 py-1 rounded">
                                {getNotificationTypeLabel(notification?.notificationType)}
                              </span>
                              <h3 className="text-white font-semibold mt-2">{notification?.contentTitle}</h3>
                            </div>
                            <span className="text-white/60 text-sm whitespace-nowrap">
                              {formatDate(notification?.emailSentAt || notification?.createdAt)}
                            </span>
                          </div>
                          
                          {notification?.contentDescription && (
                            <p className="text-white/70 text-sm mb-2">{notification?.contentDescription}</p>
                          )}

                          {notification?.musicalTitle && (
                            <p className="text-white/60 text-sm">
                              Musical: <span className="text-purple-300">{notification?.musicalTitle}</span>
                            </p>
                          )}

                          {notification?.songTitle && (
                            <p className="text-white/60 text-sm">
                              Song: <span className="text-purple-300">{notification?.songTitle}</span>
                            </p>
                          )}

                          <div className="flex items-center gap-2 mt-2">
                            <span className={`text-xs px-2 py-1 rounded ${
                              notification?.emailStatus === 'sent' || notification?.emailStatus === 'delivered'
                                ? 'bg-green-500/20 text-green-300'
                                : notification?.emailStatus === 'failed' ?'bg-red-500/20 text-red-300' :'bg-yellow-500/20 text-yellow-300'
                            }`}>
                              {notification?.emailStatus || 'pending'}
                            </span>
                          </div>

                          {notification?.errorMessage && (
                            <div className="mt-2 text-red-300 text-xs bg-red-500/10 rounded p-2">
                              Error: {notification?.errorMessage}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentNotificationCenter;